import React, { useRef, useEffect, useState } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Dimensions,
  Animated,
  Alert,
  Platform,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { Opportunity } from "../types";
import { format } from "date-fns";
import { useFavorites } from "../contexts/FavoritesContext";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import {
  calculateOpportunityStatus,
  getDaysUntilDate,
} from "../utils/filterUtils";
import AuthRequiredModal from "./auth/AuthRequiredModal";

interface OpportunityCardProps {
  opportunity: Opportunity;
  onPress: (id: string) => void;
  index?: number;
  showConfetti?: boolean; // New prop to trigger confetti
}

const { width } = Dimensions.get("window");
const numColumns = width > 1200 ? 3 : width > 800 ? 2 : 1;
const isTabletOrDesktop = width >= 768;
const isSmallMobile = width < 380;

// Confetti particle component
const ConfettiParticle: React.FC<{
  color: string;
  delay: number;
  duration: number;
  startX: number;
  endX: number;
  startY: number;
  endY: number;
}> = ({ color, delay, duration, startX, endX, startY, endY }) => {
  const translateX = useRef(new Animated.Value(startX)).current;
  const translateY = useRef(new Animated.Value(startY)).current;
  const rotate = useRef(new Animated.Value(0)).current;
  const opacity = useRef(new Animated.Value(1)).current;

  useEffect(() => {
    const animation = Animated.parallel([
      Animated.timing(translateX, {
        toValue: endX,
        duration: duration,
        delay: delay,
        useNativeDriver: true,
      }),
      Animated.timing(translateY, {
        toValue: endY,
        duration: duration,
        delay: delay,
        useNativeDriver: true,
      }),
      Animated.timing(rotate, {
        toValue: 360,
        duration: duration,
        delay: delay,
        useNativeDriver: true,
      }),
      Animated.timing(opacity, {
        toValue: 0,
        duration: duration * 0.8,
        delay: delay + duration * 0.2,
        useNativeDriver: true,
      }),
    ]);

    animation.start();
  }, []);

  return (
    <Animated.View
      style={[
        styles.confettiParticle,
        {
          backgroundColor: color,
          transform: [
            { translateX },
            { translateY },
            {
              rotate: rotate.interpolate({
                inputRange: [0, 360],
                outputRange: ["0deg", "360deg"],
              }),
            },
          ],

          opacity,
        },
      ]}
      data-oid="r7kxodw"
    />
  );
};

// Main confetti component
const ConfettiEffect: React.FC<{
  show: boolean;
  cardWidth: number;
  cardHeight: number;
}> = ({ show, cardWidth, cardHeight }) => {
  const [particles, setParticles] = useState<
    Array<{
      id: string;
      color: string;
      delay: number;
      duration: number;
      startX: number;
      endX: number;
      startY: number;
      endY: number;
    }>
  >([]);
  const [animationKey, setAnimationKey] = useState(0);

  const confettiColors = [
    "#FF6B6B",
    "#4ECDC4",
    "#45B7D1",
    "#96CEB4",
    "#FECA57",
    "#FF9FF3",
    "#54A0FF",
    "#5F27CD",
    "#00D2D3",
    "#FF9F43",
    "#F368E0",
    "#3742FA",
    "#2F3542",
    "#57606F",
    "#A4B0BE",
  ];

  const createConfetti = () => {
    const newParticles: Array<{
      id: string;
      color: string;
      delay: number;
      duration: number;
      startX: number;
      endX: number;
      startY: number;
      endY: number;
    }> = [];
    const particleCount = 20;

    for (let i = 0; i < particleCount; i++) {
      const delay = Math.random() * 1000;
      const duration = 2000 + Math.random() * 1500;
      const startX = Math.random() * cardWidth;
      const startY = -20 - Math.random() * 50; // Start above the card
      const endX = startX + (Math.random() - 0.5) * 150;
      const endY = cardHeight + 50 + Math.random() * 100;
      const color =
        confettiColors[Math.floor(Math.random() * confettiColors.length)];

      newParticles.push({
        id: `${animationKey}-${i}`,
        color,
        delay,
        duration,
        startX,
        endX,
        startY,
        endY,
      });
    }

    setParticles((prev) => [...prev, ...newParticles]);
  };

  useEffect(() => {
    if (show && cardWidth > 0 && cardHeight > 0) {
      // Initial confetti burst
      createConfetti();

      // Continuous confetti generation
      const interval = setInterval(() => {
        createConfetti();
        setAnimationKey((prev) => prev + 1);
      }, 2500); // New confetti every 2.5 seconds

      return () => clearInterval(interval);
    }
  }, [show, cardWidth, cardHeight]);

  // Clean up finished particles
  useEffect(() => {
    const cleanup = setTimeout(() => {
      setParticles((prev) =>
        prev.filter((p) => {
          const creationTime = parseInt(p.id.split("-")[0]);
          return animationKey - creationTime < 2; // Keep particles from last 2 cycles
        }),
      );
    }, 4000);

    return () => clearTimeout(cleanup);
  }, [animationKey]);

  if (!show) return null;

  return (
    <View style={styles.confettiContainer} data-oid="w90o124">
      {particles.map((particle) => (
        <ConfettiParticle
          key={particle.id}
          color={particle.color}
          delay={particle.delay}
          duration={particle.duration}
          startX={particle.startX}
          endX={particle.endX}
          startY={particle.startY}
          endY={particle.endY}
          data-oid="lj_5qz:"
        />
      ))}
    </View>
  );
};

const OpportunityCard: React.FC<OpportunityCardProps> = ({
  opportunity,
  onPress,
  index = 0,
  showConfetti = false,
}) => {
  const { theme } = useTheme();
  const cardBg = theme === "dark" ? "#232327" : "#fff";
  const cardBorder = theme === "dark" ? "#34343B" : "#E4E4E7";
  const headerText = theme === "dark" ? "#F2F2F2" : "#18181B";
  const subText = theme === "dark" ? "#C2C2C2" : "#71717A";
  const infoText = theme === "dark" ? "#C2C2C2" : "#52525B";
  const descText = theme === "dark" ? "#C2C2C2" : "#52525B";
  const footerBg = theme === "dark" ? "#202024" : "#F4F4F5";
  const tagBg = theme === "dark" ? "#232327" : "#F4F4F5";
  const tagText = theme === "dark" ? "#C2C2C2" : "#52525B";
  const btnBg = theme === "dark" ? "#34343B" : "#F4F4F5";
  const btnBorder = theme === "dark" ? "#2D2D34" : "#E4E4E7";
  const btnText = theme === "dark" ? "#FAFAFA" : "#272727";
  const bookmarkActiveColor = theme === "dark" ? "#FFFFFF" : "#272727";
  const bookmarkInactiveColor = theme === "dark" ? "#71717A" : "#A1A1AA";
  // Authentication and favorites
  const { isAuthenticated } = useAuth();
  const { saveOpportunity, unsaveOpportunity, isSaved } = useFavorites();
  const [isFavorite, setIsFavorite] = useState(isSaved(opportunity.id));
  const [showAuthModal, setShowAuthModal] = useState(false);

  const scaleAnim = useRef(new Animated.Value(0.95)).current;
  const opacityAnim = useRef(new Animated.Value(0)).current;
  const favoriteAnim = useRef(new Animated.Value(1)).current;
  const glowAnim = useRef(new Animated.Value(0)).current;

  // Card dimensions for confetti
  const [cardDimensions, setCardDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const animations: Animated.CompositeAnimation[] = [];
    const animationValues = [scaleAnim, opacityAnim, favoriteAnim, glowAnim];

    const delay = index * 80 > 800 ? 800 : index * 80;
    const entranceAnimation = Animated.parallel([
      Animated.timing(opacityAnim, {
        toValue: 1,
        duration: 400,
        delay,
        useNativeDriver: true,
      }),
      Animated.spring(scaleAnim, {
        toValue: 1,
        friction: 8,
        tension: 40,
        delay,
        useNativeDriver: true,
      }),
    ]);
    animations.push(entranceAnimation);
    entranceAnimation.start();

    // Start glow animation if this is a special opportunity
    let glowAnimation: Animated.CompositeAnimation | null = null;
    if (showConfetti) {
      glowAnimation = Animated.loop(
        Animated.sequence([
          Animated.timing(glowAnim, {
            toValue: 1,
            duration: 2000,
            useNativeDriver: false,
          }),
          Animated.timing(glowAnim, {
            toValue: 0,
            duration: 2000,
            useNativeDriver: false,
          }),
        ]),
      );
      animations.push(glowAnimation);
      glowAnimation.start();
    }

    // Cleanup function for animations
    return () => {
      // Stop all running animations
      animations.forEach((animation) => {
        try {
          animation.stop();
        } catch (error) {
          console.warn("OpportunityCard animation cleanup error:", error);
        }
      });

      // Reset animation values to prevent memory leaks
      animationValues.forEach((animValue) => {
        try {
          animValue.stopAnimation();
          animValue.removeAllListeners();
        } catch (error) {
          console.warn("OpportunityCard animation value cleanup error:", error);
        }
      });
    };
  }, [showConfetti, index, scaleAnim, opacityAnim, favoriteAnim, glowAnim]);

  useEffect(() => {
    setIsFavorite(isSaved(opportunity.id));
  }, [opportunity.id, isSaved]);

  const handleToggleFavorite = async (e: any) => {
    e.stopPropagation();
    
    // Animate the button press
    Animated.sequence([
      Animated.timing(favoriteAnim, {
        toValue: 0.8,
        duration: 100,
        useNativeDriver: true,
      }),
      Animated.timing(favoriteAnim, {
        toValue: 1,
        duration: 100,
        useNativeDriver: true,
      }),
    ]).start();

    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }

    if (isFavorite) {
      await unsaveOpportunity(opportunity.id);
    } else {
      await saveOpportunity(opportunity.id);
    }
    setIsFavorite(!isFavorite);
  };

  const handleCardPress = (e?: any) => {
    // On web the card title is a real <a href> (for crawlability); intercept the
    // click so we open the detail modal instead of doing a full-page navigation.
    if (Platform.OS === "web" && e && typeof e.preventDefault === "function") {
      e.preventDefault();
    }
    onPress(opportunity.id);
  };

  // Props that make the title render as a crawlable <a href> on web while
  // staying a plain heading on native.
  const titleLinkProps =
    Platform.OS === "web"
      ? ({ href: `/opportunity/${opportunity.id}`, accessibilityRole: "link" } as any)
      : {};

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return null; // Return null instead of "N/A" for conditional rendering
    try {
      return format(new Date(dateString), "MMM d, yyyy");
    } catch (error) {
      console.error("Error formatting date:", dateString, error);
      return null; // Return null for invalid dates too
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case "Hackathon":
        return "code-slash-outline";
      case "Competition":
        return "trophy-outline";
      case "Grant":
        return "cash-outline";
      case "Scholarship":
        return "school-outline";
      case "Conference":
        return "people-outline";
      case "Workshop/Programs":
        return "construct-outline";
      case "Internship":
        return "briefcase-outline";
      default:
        return "star-outline";
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case "Hackathon":
        return "#F43F5E";
      case "Competition":
        return "#3B82F6";
      case "Grant":
        return "#8B5CF6";
      case "Scholarship":
        return "#10B981";
      case "Conference":
        return "#334155";
      case "Workshop/Programs":
        return "#F59E0B";
      case "Internship":
        return "#06B6D4";
      default:
        return "#71717A";
    }
  };

  const calculatedStatus = calculateOpportunityStatus(opportunity);

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "Open":
        return "APPLY NOW";
      case "Coming Soon":
        return "COMING SOON";
      case "Ongoing":
        return "ONGOING";
      case "Closed":
        return "CLOSED";
      default:
        return status ? status.toUpperCase() : "UNDEFINED";
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Open":
        return "#10B981";
      case "Coming Soon":
        return "#F59E0B";
      case "Ongoing":
        return "#3B82F6";
      case "Closed":
        return "#EF4444";
      default:
        return "#71717A";
    }
  };

  const renderOpportunityTypes = () => {
    const types = Array.isArray(opportunity.type)
      ? opportunity.type
      : opportunity.type.split(",").map((t) => t.trim());
    const primaryType = types[0];
    const hasMultipleTypes = types.length > 1;
    return (
      <View
        style={[
          styles.typeBadge,
          { backgroundColor: getTypeColor(primaryType as string) + "15" },
        ]}
        data-oid="6ud0:qk"
      >
        <Ionicons
          name={getTypeIcon(primaryType as string)}
          size={15}
          color={getTypeColor(primaryType as string)}
          data-oid="u6xtk-l"
        />

        <Text
          style={[
            styles.typeBadgeText,
            { color: getTypeColor(primaryType as string) },
          ]}
          data-oid="14e-dh:"
        >
          {primaryType}
          {hasMultipleTypes ? ` +${types.length - 1}` : ""}
        </Text>
      </View>
    );
  };

  const getInteractionIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case "in-person":
        return "person-outline";
      case "virtual":
        return "laptop-outline";
      case "hybrid":
        return "git-merge-outline";
      default:
        return "help-circle-outline";
    }
  };

  const glowColor = glowAnim.interpolate({
    inputRange: [0, 1],
    outputRange: ["rgba(255, 215, 0, 0)", "rgba(255, 215, 0, 0.3)"],
  });

  return (
    <Animated.View
      style={[
        styles.cardWrapper,
        { opacity: opacityAnim, transform: [{ scale: scaleAnim }] },
      ]}
      data-oid="gaknvsr"
    >
      <Animated.View
        style={[
          styles.glowWrapper,
          showConfetti && {
            shadowColor: "#FFD700",
            shadowOffset: { width: 0, height: 0 },
            shadowOpacity: glowAnim,
            shadowRadius: 20,
            elevation: 10,
          },
        ]}
        onLayout={(event) => {
          const { width, height } = event.nativeEvent.layout;
          setCardDimensions({ width, height });
        }}
        data-oid="ywi3e7h"
      >
        <TouchableOpacity
          style={[
            styles.card,
            {
              backgroundColor: cardBg,
              borderTopColor: Array.isArray(opportunity.type)
                ? getTypeColor(opportunity.type[0] as string)
                : getTypeColor(
                    (opportunity.type.split(",")[0] || "").trim() as string,
                  ),
              borderColor: cardBorder,
              // Add special styling for spotlight opportunities
              ...(showConfetti && {
                borderWidth: 2,
                borderColor: "#FFD700",
              }),
            },
          ]}
          onPress={handleCardPress}
          activeOpacity={0.96}
          data-oid="kd248up"
        >
          {/* Header - Type Badge and Status */}
          <View style={styles.cardHeader} data-oid="v5qdoe_">
            {renderOpportunityTypes()}
            <View
              style={[
                styles.statusBadge,
                {
                  backgroundColor: getStatusColor(calculatedStatus || 'Open') + "15",
                  borderColor: getStatusColor(calculatedStatus || 'Open') + "30",
                },
              ]}
              data-oid="fl7a-:l"
            >
              <View
                style={[
                  styles.statusDot,
                  { backgroundColor: getStatusColor(calculatedStatus || 'Open') },
                ]}
                data-oid="vctbrs3"
              />

              <Text
                style={[
                  styles.statusText,
                  { color: getStatusColor(calculatedStatus || 'Open') },
                ]}
                data-oid="llpnmoz"
              >
                {getStatusLabel(calculatedStatus || 'Open')}
              </Text>
            </View>
          </View>

          {/* Main Content */}
          <View style={styles.cardContent} data-oid="xaor:y.">
            {/* Title and Org */}
            <Text
              style={[styles.organizationText, { color: subText }]}
              data-oid="8zble-o"
            >
              {opportunity.organization}
            </Text>
            <View style={styles.titleRow} data-oid="qy1es:z">
              <Text
                accessibilityRole="header"
                aria-level={3}
                style={[styles.title, { color: headerText }]}
                numberOfLines={2}
                data-oid="wxj6175"
              >
                <Text
                  {...titleLinkProps}
                  onPress={handleCardPress}
                  style={[styles.title, { color: headerText }]}
                >
                  {opportunity.title}
                </Text>
              </Text>
              {showConfetti && (
                <View style={styles.spotlightBadge} data-oid="-8ruqm4">
                  <Ionicons
                    name="star"
                    size={16}
                    color="#FFD700"
                    data-oid="8g3jvil"
                  />
                </View>
              )}
            </View>

            {/* Key Info */}
            <View style={styles.infoContainer} data-oid="z:ibp1z">
              {/* Only show date row if we have valid dates */}
              {(formatDate(opportunity.startDate) || formatDate(opportunity.endDate)) && (
                <View style={styles.infoRow} data-oid="wz242fz">
                  <Ionicons
                    name="calendar-outline"
                    size={16}
                    color={infoText}
                    data-oid="wf67v4:"
                  />

                  <Text
                    style={[styles.infoText, { color: infoText }]}
                    numberOfLines={1}
                    data-oid="38409qi"
                  >
                    {formatDate(opportunity.startDate) || "TBD"}
                    {opportunity.endDate !== opportunity.startDate &&
                      formatDate(opportunity.endDate) &&
                      ` - ${formatDate(opportunity.endDate)}`}
                  </Text>
                </View>
              )}

              {/* Only show location row if location exists */}
              {opportunity.location && (
                <View style={styles.infoRow} data-oid="zh96el5">
                  <Ionicons
                    name="location-outline"
                    size={16}
                    color={infoText}
                    data-oid=":pq8ymr"
                  />

                  <Text
                    style={[styles.infoText, { color: infoText }]}
                    numberOfLines={1}
                    data-oid=".25f0_g"
                  >
                    {opportunity.location}
                  </Text>
                </View>
              )}

              {/* Only show deadline row if deadline exists and is valid */}
              {opportunity.deadline && formatDate(opportunity.deadline) && (
                <View style={styles.infoRow} data-oid=".w0vmlu">
                  <Ionicons
                    name="time-outline"
                    size={16}
                    color={infoText}
                    data-oid="swxe7c4"
                  />

                  <Text
                    style={[styles.infoText, { color: infoText }]}
                    data-oid="3s9a:yb"
                  >
                    Deadline: {formatDate(opportunity.deadline)}
                  </Text>
                </View>
              )}

              {opportunity.gradeLevel && opportunity.gradeLevel.length > 0 && (
                <View style={styles.infoRow} data-oid=".4f5ary">
                  <Ionicons
                    name="school-outline"
                    size={16}
                    color={infoText}
                    data-oid="ttavdfi"
                  />

                  <Text
                    style={[styles.infoText, { color: infoText }]}
                    data-oid="3w6c1dy"
                  >
                    For:{" "}
                    {opportunity.gradeLevel.length === 1
                      ? opportunity.gradeLevel[0]
                      : `${opportunity.gradeLevel[0]} +${opportunity.gradeLevel.length - 1}`}
                  </Text>
                </View>
              )}
            </View>

            {/* Description */}
            <Text
              style={[styles.description, { color: descText }]}
              numberOfLines={3}
              data-oid="fsv60_:"
            >
              {opportunity.description}
            </Text>
          </View>

          {/* Card Footer */}
          <View
            style={[styles.cardFooter, { borderTopColor: cardBorder }]}
            data-oid="32dl-_e"
          >
            {/* Special Tags */}
            <View style={styles.footerTags} data-oid="uvsuvmz">
              {opportunity.interactionTypes &&
                opportunity.interactionTypes.length > 0 && (
                  <View
                    style={[styles.tag, { backgroundColor: tagBg }]}
                    data-oid="i.swd3j"
                  >
                    <Ionicons
                      name={getInteractionIcon(opportunity.interactionTypes[0])}
                      size={14}
                      color={tagText}
                      data-oid="c8b9p1t"
                    />

                    <Text
                      style={[styles.tagText, { color: tagText }]}
                      data-oid="_lr--zc"
                    >
                      {opportunity.interactionTypes[0]}
                    </Text>
                  </View>
                )}

              {(opportunity.applicationCloseDate || opportunity.deadline) &&
                (calculatedStatus || 'Open') === "Open" && (
                  <View
                    style={[styles.tag, { backgroundColor: "#FEF3C7" }]}
                    data-oid="6w6g:25"
                  >
                    <Ionicons
                      name="hourglass-outline"
                      size={14}
                      color="#D97706"
                      data-oid="s4a8b38"
                    />

                    {/* <Text
                      style={[styles.tagText, { color: "#D97706" }]}
                      data-oid="6b_1eo9"
                    >
                      {getDaysUntilDate(
                        opportunity.applicationCloseDate ||
                          opportunity.deadline,
                      ) === `3 days left`
                        ? "Apply soon"
                        : "Apply now"}
                    </Text> */}
                  </View>
                )}
            </View>

            {/* Actions */}
            <View style={styles.footerActions} data-oid="wp_cfsx">
              <TouchableOpacity
                style={styles.favoriteButton}
                onPress={handleToggleFavorite}
                data-oid="gtrb15v"
              >
                <Animated.View
                  style={{ transform: [{ scale: favoriteAnim }] }}
                  data-oid="n-w.3lz"
                >
                  <Ionicons
                    name={isFavorite && isAuthenticated ? "bookmark" : "bookmark-outline"}
                    size={22}
                    color={isFavorite && isAuthenticated ? bookmarkActiveColor : bookmarkInactiveColor}
                    data-oid="du_t99n"
                  />
                </Animated.View>
              </TouchableOpacity>

              <TouchableOpacity
                style={[
                  styles.detailsButton,
                  { backgroundColor: btnBg, borderColor: btnBorder },
                ]}
                onPress={() => onPress(opportunity.id)}
                data-oid="kakl-3w"
              >
                <Text
                  style={[styles.detailsText, { color: btnText }]}
                  data-oid="p16qm5k"
                >
                  Details
                </Text>
                <Ionicons
                  name="chevron-forward"
                  size={14}
                  color={btnText}
                  data-oid="q:_:8f9"
                />
              </TouchableOpacity>
            </View>
          </View>

          {/* Claimed badge */}
          {opportunity.isClaimed && (
            <View style={styles.featuredBadge} data-oid="8677toj">
              <Ionicons
                name="shield-checkmark"
                size={16}
                color="#fff"
                data-oid=".pgou28"
              />
            </View>
          )}
        </TouchableOpacity>
      </Animated.View>

      {/* Auth Required Modal */}
      <AuthRequiredModal
        visible={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        featureName="saved opportunities"
      />
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  cardWrapper: {
    padding: 8,
    marginBottom: 8,
    width:
      numColumns === 1
        ? "100%"
        : numColumns === 2
          ? isTabletOrDesktop
            ? "50%"
            : "100%"
          : isTabletOrDesktop
            ? "33.333%"
            : "100%",
  },
  glowWrapper: {
    borderRadius: 16,
    position: "relative",
  },
  card: {
    backgroundColor: "#fff",
    borderRadius: 16,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.04,
    shadowRadius: 4,
    elevation: 1,
    overflow: "hidden",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    position: "relative",
    borderTopWidth: 4,
    borderWidth: 1,
  },
  cardHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 10,
  },
  cardContent: {
    flex: 1,
    padding: 16,
    paddingTop: 0,
  },
  typeBadge: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 20,
  },
  typeBadgeText: {
    fontSize: 13,
    fontWeight: "600",
    marginLeft: 5,
    fontFamily: "Inter-SemiBold",
  },
  statusBadge: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 20,
    borderWidth: 1,
  },
  statusDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    marginRight: 4,
  },
  statusText: {
    fontSize: 10,
    fontWeight: "700",
    letterSpacing: 0.5,
    fontFamily: "Inter-Bold",
  },
  organizationText: {
    fontSize: 13,
    color: "#71717A",
    marginBottom: 4,
    fontFamily: "Inter-Medium",
  },
  titleRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: 12,
  },
  title: {
    fontSize: isSmallMobile ? 15 : 17,
    fontWeight: "bold",
    color: "#18181B",
    lineHeight: isSmallMobile ? 20 : 22,
    fontFamily: "Inter-Bold",
    flex: 1,
    marginRight: 8,
  },
  spotlightBadge: {
    backgroundColor: "#FFF9E6",
    borderRadius: 12,
    padding: 4,
    borderWidth: 1,
    borderColor: "#FFD700",
  },
  infoContainer: {
    marginBottom: 12,
  },
  infoRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8,
  },
  infoText: {
    fontSize: 13,
    color: "#52525B",
    marginLeft: 12,
    fontFamily: "Inter-Regular",
  },
  description: {
    fontSize: 14,
    color: "#52525B",
    lineHeight: 20,
    fontFamily: "Inter-Regular",
  },
  cardFooter: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderTopWidth: 1,
    borderTopColor: "#F4F4F5",
  },
  footerTags: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 8,
  },
  tag: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#F4F4F5",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    gap: 4,
  },
  iconTag: {
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#F4F4F5",
    padding: 4,
    borderRadius: 12,
    width: 24,
    height: 24,
  },
  tagText: {
    fontSize: 12,
    color: "#52525B",
    fontFamily: "Inter-Medium",
  },
  footerActions: {
    flexDirection: "row",
    alignItems: "center",
  },
  favoriteButton: {
    padding: 8,
    marginRight: 8,
  },
  detailsButton: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
    backgroundColor: "#F4F4F5",
    borderWidth: 1,
    borderColor: "#E4E4E7",
  },
  detailsText: {
    fontSize: 13,
    color: "#272727",
    fontWeight: "600",
    marginRight: 4,
    fontFamily: "Inter-SemiBold",
  },
  featuredBadge: {
    position: "absolute",
    top: 0,
    right: 0,
    backgroundColor: "#10B981",
    padding: 6,
    borderBottomLeftRadius: 12,
  },
  confettiContainer: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    pointerEvents: "none",
    overflow: "hidden",
    borderRadius: 16,
  },
  confettiParticle: {
    position: "absolute",
    width: 8,
    height: 8,
    borderRadius: 4,
  },
});

export default React.memo(OpportunityCard, (prevProps, nextProps) => {
  return (
    prevProps.opportunity.id === nextProps.opportunity.id &&
    prevProps.opportunity.title === nextProps.opportunity.title &&
    prevProps.index === nextProps.index &&
    prevProps.showConfetti === nextProps.showConfetti
  );
});
